import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { AdminListShell } from "@/components/admin/AdminListShell";
import { grantRole, revokeRole, type TeamMember } from "@/lib/team.functions";
import { adminTeamQuery, INVALIDATE } from "@/lib/query-registry";
import { useAuthedMutation } from "@/lib/authed-mutation";

export const Route = createFileRoute("/admin/team")({
  component: TeamPage,
});

function TeamPage() {
  const query = useQuery(adminTeamQuery());
  const members = (query.data?.members ?? []) as TeamMember[];

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "moderator">("admin");
  const [success, setSuccess] = useState<string | null>(null);

  const grant = useAuthedMutation(grantRole, {
    invalidate: [INVALIDATE.adminTeam],
    onSuccess: (data) => {
      setSuccess(`Granted ${role} to ${data.email}`);
      setEmail("");
    },
  });

  const revoke = useAuthedMutation(revokeRole, {
    invalidate: [INVALIDATE.adminTeam],
  });

  const onGrant = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    grant.mutate({ email, role });
  };

  const onRevoke = (m: TeamMember) => {
    if (!confirm(`Revoke ${m.role} from ${m.email}?`)) return;
    setSuccess(null);
    revoke.mutate(
      { user_id: m.user_id, role: m.role },
      { onSuccess: () => setSuccess(`Revoked ${m.role} from ${m.email}`) },
    );
  };

  const error = grant.error?.message ?? revoke.error?.message ?? null;

  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">Team</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Grant or revoke admin access. Teammates must first sign up at{" "}
        <code className="rounded bg-card px-1.5 py-0.5 text-xs">/admin/login</code>, then you can
        grant their role here.
      </p>

      <section className="mt-8 rounded-2xl border border-border bg-card/30 p-6">
        <h2 className="font-display text-lg font-semibold text-foreground">Grant role</h2>
        <form onSubmit={onGrant} className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
          <input
            type="email"
            required
            placeholder="teammate@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:border-foreground focus:outline-none"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as "admin" | "moderator")}
            className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:border-foreground focus:outline-none"
          >
            <option value="admin">admin</option>
            <option value="moderator">moderator (PULSE)</option>
          </select>
          <button
            type="submit"
            disabled={grant.isPending}
            className="rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {grant.isPending ? "Granting…" : "Grant"}
          </button>
        </form>
        {error && (
          <p className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}
        {success && (
          <p className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400">
            {success}
          </p>
        )}
      </section>

      <section className="mt-10">
        <h2 className="font-display text-lg font-semibold text-foreground">
          Members ({members.length})
        </h2>
        <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-card/30">
          <AdminListShell
            query={query}
            isEmpty={(d) => (d.members ?? []).length === 0}
            emptyText="No roles granted yet."
          >
            {() => (
              <ul className="divide-y divide-border">
                {members.map((m) => (
                  <li
                    key={`${m.user_id}-${m.role}`}
                    className="flex items-center justify-between gap-4 px-5 py-4"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{m.email}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        <span className="rounded-full bg-foreground/10 px-2 py-0.5 font-mono uppercase tracking-wider">
                          {m.role}
                        </span>
                        <span className="ml-2">
                          granted {new Date(m.granted_at).toLocaleDateString()}
                        </span>
                      </p>
                    </div>
                    <button
                      onClick={() => onRevoke(m)}
                      disabled={revoke.isPending}
                      className="shrink-0 rounded-full border border-border px-4 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-destructive hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                    >
                      Revoke
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </AdminListShell>
        </div>
      </section>
    </div>
  );
}
